import React, { useEffect, useState } from "react";
import * as AppConstants from "AppConstants";
import { getCurrentClient, checkUserHasClientRole } from "app/services/app.state.service";
import { Alert, Card, Grid, Snackbar } from "@mui/material";
import { HolidayListView } from "app/components/leave-plans/HolidayListView";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { loadUnifiedEffortsMonthly } from "app/services/report.service";
import { loadMembersFromBackendForClient } from "app/services/member.service";
import { getDateInDDMMYYYYFormat } from "utils/dateUtil";
import { resolveOwnerParams } from "utils/effortOwnerParams";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import { handleAxiosError } from "app/services/app.error.handler";
import { ActivityStatsChartsRow } from "app/components/dashboards/ActivityStatsChartsRow";
import { LeavePlanSummaryCard } from "app/components/leave-plans/LeavePlanSummaryCard";
import { ConsolidatedReportFilter } from "app/components/reports/ConsolidatedReportFilter";
import { NoDataBanner } from "app/components/dashboards/NoDataBanner";
import "./dashboards.css";

export const ClientDashboard = () => {
  const [clientId, setClientId] = useState();
  const [profileMissing, setProfileMissing] = useState(false);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [pendingPeriod, setPendingPeriod] = useState(defaultPeriod);
  const [pendingUser, setPendingUser] = useState(AppConstants.ALL_USERS);
  // Only the range survives Apply — it labels the card. The user selection travels into the query.
  const [appliedRange, setAppliedRange] = useState(() => periodRange(defaultPeriod()));
  const [allRows, setAllRows] = useState([]);

  useEffect(() => {
    if (!checkUserHasClientRole()) return;
    getCurrentClient()
      .then((client) => {
        if (!client || !client.id) {
          setProfileMissing(true);
          return;
        }
        setClientId(client.id);
      })
      .catch(() => setProfileMissing(true));
  }, []);

  // The selected user is part of the query now, so the response contains only their rows rather
  // than every member of the client.
  const apply = React.useCallback(
    (id, range, user, memberList) => {
      if (id == null) return;
      setIsLoading(true);
      loadUnifiedEffortsMonthly({
        fromDate: range.from,
        toDate: range.to,
        clientIds: [id],
        ...resolveOwnerParams(user, memberList),
      })
        .then((r) => {
          setIsLoading(false);
          setAllRows(r.status === 204 || !Array.isArray(r.data) ? [] : r.data);
        })
        .catch((error) => {
          setIsLoading(false);
          setAllRows([]);
          handleAxiosError(error);
        });
    },
    []
  );

  useEffect(() => {
    if (clientId == null) return;
    loadMembersFromBackendForClient(clientId)
      .then((r) => setMembers(r.status === 204 || !Array.isArray(r.data) ? [] : r.data))
      .catch(() => setMembers([]));
    apply(clientId, appliedRange, AppConstants.ALL_USERS, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const { memberOptions } = useUserFilterOptions({ members });
  // Already scoped by the query.
  const displayRows = allRows;

  const onApply = () => {
    const range = periodRange(pendingPeriod);
    setAppliedRange(range);
    apply(clientId, range, pendingUser, members);
  };

  if (!checkUserHasClientRole()) {
    return <div>You do not have access to this functionality.</div>;
  }

  return (
    <div className="content-container dashboard-container">
      <Grid container rowSpacing={10} columnSpacing={0}>
        <Grid size={12}>
          <Card>
            <MDBox mx={2} mt={-3} py={1} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
              <MDTypography variant="h6" color="white">
                {`Efforts summary for period - ${getDateInDDMMYYYYFormat(appliedRange.from)} and ${getDateInDDMMYYYYFormat(
                  appliedRange.to
                )}`}
              </MDTypography>
            </MDBox>
            <MDBox px={2} pt={3}>
              {/* No client filter: a client user only ever sees their own client. */}
              <EffortFilterBar
                idPrefix="client-effort"
                showMemberManagerDropdown
                userValue={pendingUser}
                onUserChange={setPendingUser}
                memberOptions={memberOptions}
                groupUsers={false}
                period={pendingPeriod}
                onPeriodChange={setPendingPeriod}
                onAction={onApply}
              />
            </MDBox>
            {isLoading ? (
              <NoDataBanner message="Loading efforts summary..." />
            ) : displayRows.length === 0 ? (
              <NoDataBanner />
            ) : (
              <ActivityStatsChartsRow
                activityStatsData={displayRows}
                fromDate={appliedRange.from}
                toDate={appliedRange.to}
              />
            )}
          </Card>
        </Grid>

        <Grid size={12}>
          <ConsolidatedReportFilter role={AppConstants.ROLE_CLIENT} />
        </Grid>
        <Grid size={12}>
          {/* No client filter: a client user only ever sees their own client. */}
          {clientId && (
            <LeavePlanSummaryCard
              idPrefix="client-leave"
              role={AppConstants.ROLE_CLIENT}
              context={{ clientId: clientId }}
              showUser
              groupUsers={false}
              members={members}
            />
          )}
        </Grid>
        <Grid size={12}>
          <HolidayListView />
        </Grid>
      </Grid>
      <Snackbar
        open={profileMissing}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={8000}
        onClose={() => setProfileMissing(false)}
      >
        <Alert severity="warning" variant="filled" onClose={() => setProfileMissing(false)} sx={{ width: "100%" }}>
          Account setup incomplete. Please contact your administrator to link your profile.
        </Alert>
      </Snackbar>
    </div>
  );
};
